/**
 * KeyMetricsGrid Component
 * 
 * Responsive grid displaying primary activity metrics.
 * Adapts layout based on viewport size.
 */

import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  Card,
  CardContent,
  Stack,
  Avatar,
  Skeleton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Timer as TimerIcon,
  Timeline,
  Terrain,
  Speed,
  Favorite,
  Bolt,
  LocalFireDepartment as EnergyIcon,
  DirectionsRun,
  DirectionsBike,
  // DataQuality as DataQualityIcon,
} from '@mui/icons-material';
import { type ActivityDetailResponse } from '../../../types';
import { formatTime, formatDistance } from '../../../utils/chartUtils';

interface KeyMetricsGridProps {
  /** Activity data */
  activity: ActivityDetailResponse | null;
  /** Whether the data is loading */
  isLoading?: boolean;
}

/**
 * Format pace from m/s to min/km
 */
function formatPace(speedMs: number): string {
  if (speedMs <= 0) return '--:--';
  const paceMinPerKm = 1000 / (speedMs * 60);
  const minutes = Math.floor(paceMinPerKm);
  const seconds = Math.floor((paceMinPerKm - minutes) * 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Format speed from m/s to km/h
 */
function formatSpeed(speedMs: number): string {
  if (speedMs <= 0) return '0';
  return (speedMs * 3.6).toFixed(1);
}

/**
 * Format duration from seconds to readable format
 */
function formatDuration(seconds: number): string {
  return formatTime(seconds);
}


// Modern Widget Component
interface MetricWidgetProps {
  title: string;
  value: string | number;
  unit?: string;
  subtitle?: string | React.ReactNode;
  icon: any;
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  bgColor?: string;
}

const MetricWidget = ({
  title,
  value,
  unit,
  subtitle,
  icon: Icon,
  color = 'primary',
  bgColor
}: MetricWidgetProps) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-2px)',
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
            {title}
          </Typography>
          <Avatar
            sx={{
              bgcolor: bgColor ? alpha(bgColor, 0.15) : alpha(theme.palette[color].main, 0.15),
              color: bgColor || theme.palette[color].main,
              width: 32,
              height: 32
            }}
          >
            <Icon sx={{ fontSize: 18 }} />
          </Avatar>
        </Stack>

        <Box>
          <Typography variant="h5" fontWeight="bold" color="text.primary" lineHeight={1.2}>
            {value}
            {unit && <Typography component="span" variant="body2" color="text.secondary" ml={0.5}>{unit}</Typography>}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * KeyMetricsGrid - Display primary activity metrics in a responsive grid
 */
export const KeyMetricsGrid: React.FC<KeyMetricsGridProps> = ({
  activity,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Activity Metrics
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Skeleton variant="text" width={80} height={16} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </Stack>
                  <Skeleton variant="text" width={60} height={28} />
                  <Skeleton variant="text" width={40} height={14} sx={{ mt: 0.5 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }

  if (!activity) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Activity Metrics
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Box
          sx={{
            py: 4,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2">
            No metrics available
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Activity Metrics
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={2}>
        {/* Duration */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricWidget
            title="Moving Time"
            value={formatDuration(activity.moving_time)}
            subtitle={activity.elapsed_time ? `Total: ${formatDuration(activity.elapsed_time)}` : ''}
            icon={TimerIcon}
            color="primary"
          />
        </Grid>

        {/* Distance */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricWidget
            title="Distance"
            value={formatDistance(activity.distance)}
            unit="km"
            icon={Timeline}
            color="info"
          />
        </Grid>

        {/* Elevation Gain */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricWidget
            title="Elevation Gain"
            value={activity.total_elevation_gain ? `+${activity.total_elevation_gain}` : '0'}
            unit="m"
            icon={Terrain}
            color="success"
            bgColor="#66bb6a"
          />
        </Grid>

        {/* Avg Pace or Speed */}
        {activity.sport_type.toLowerCase().includes('run') ? (
          <Grid item xs={12} sm={6} md={3}>
            <MetricWidget
              title="Avg Pace"
              value={formatPace(activity.average_speed)}
              unit="/km"
              icon={Speed}
              color="info"
            />
          </Grid>
        ) : (
          <Grid item xs={12} sm={6} md={3}>
            <MetricWidget
              title="Avg Speed"
              value={formatSpeed(activity.average_speed)}
              unit="km/h"
              icon={Speed}
              color="info"
            />
          </Grid>
        )}

        {/* Max Speed (if available) */}
        {activity.max_speed && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricWidget
              title={activity.sport_type.toLowerCase().includes('run') ? "Max Pace" : "Max Speed"}
              value={activity.sport_type.toLowerCase().includes('run') ? formatPace(activity.max_speed) : formatSpeed(activity.max_speed)}
              unit={activity.sport_type.toLowerCase().includes('run') ? "/km" : "km/h"}
              subtitle="Peak performance"
              icon={Speed}
              color="info"
              bgColor="#42a5f5"
            />
          </Grid>
        )}

        {/* Avg Cadence */}
        {activity.average_cadence && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricWidget
              title="Avg Cadence"
              value={activity.average_cadence.toFixed(0)}
              unit={activity.sport_type.toLowerCase().includes('run') ? 'spm' : 'rpm'}
              icon={activity.sport_type.toLowerCase().includes('run') ? DirectionsRun : DirectionsBike}
              color="info"
            />
          </Grid>
        )}

        {/* Avg Heart Rate */}
        {activity.average_heartrate && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricWidget
              title="Avg Heart Rate"
              value={activity.average_heartrate.toFixed(0)}
              unit="bpm"
              subtitle={activity.max_heartrate ? `Max: ${activity.max_heartrate.toFixed(0)} bpm` : ''}
              icon={Favorite}
              color="error"
            />
          </Grid>
        )}

        {/* Avg Power */}
        {activity.average_watts && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricWidget
              title="Avg Power"
              value={activity.average_watts.toFixed(0)}
              unit="W"
              icon={Bolt}
              color="secondary"
            />
          </Grid>
        )}

        {/* Max Watts */}
        {activity.max_watts && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricWidget
              title="Max Power"
              value={activity.max_watts.toFixed(0)}
              unit="W"
              subtitle="Peak output"
              icon={Bolt}
              color="secondary"
              bgColor="#ab47bc"
            />
          </Grid>
        )}

        {/* Calories */}
        {activity.calories && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricWidget
              title="Calories"
              value={activity.calories.toFixed(0)}
              unit="kcal"
              subtitle="Energy burned"
              icon={EnergyIcon}
              color="warning"
            />
          </Grid>
        )}

        {/* Training Stress Score */}
        {activity.tss && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricWidget
              title="Training Stress"
              value={activity.tss.toFixed(0)}
              unit="TSS"
              subtitle="Training load"
              icon={Timeline}
              color="info"
            />
          </Grid>
        )}

        {/* Grade Adjusted Pace */}
        {activity.grade_adjusted_avg_speed && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricWidget
              title="Grade Adj. Pace"
              value={formatPace(activity.grade_adjusted_avg_speed)}
              unit="/km"
              subtitle={activity.grade_adjusted_max_speed ? `Max: ${formatPace(activity.grade_adjusted_max_speed)} /km` : 'Hill adjusted pace'}
              icon={Terrain}
              color="info"
              bgColor="#00bcd4"
            />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default KeyMetricsGrid;